import { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { apiClient } from 'core/api/globals';
import { Form, FormElement } from 'core/forms';
import { useEvent } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Accordion, Button, ButtonVariants } from 'core/ui';
import { hasText } from 'core/utils';

import { SettingsHelpers, useUserSettings } from 'features/settings';
import { WellKnownSettingName } from 'features/settings/types/WellKnownSettingName';
import { TemplateType } from 'features/template';

import { CoordinatorInfoAccordion } from '../../exam';
import { PatientInfoAccordion } from '../../patient';
import { PriorExamsAccordion } from '../../prior-exam';
import { ExamReadSubmitType } from '../constants';
import { ExamTypeService, InitialValuesService } from '../services';
import { ExamDetailsAccordion } from './ExamDetailsAccordion';
import { ResultsAccordion } from './ResultsAccordion';
import { TagsFieldAccordion } from './TagsFieldAccordion';

const ExamReadSidebarBody = ({ exam, handleSubmit, handleNextExamClick, handlePrevExamClick, isUndo /*, adminTags*/ }) => {
  const { userSettings } = useUserSettings();
  const isPreviewFlow = useMemo(
    () => ExamTypeService.IsPathology(exam?.service?.description) || ExamTypeService.IsPulmonology(exam?.service?.description),
    [exam],
  );

  const initialState = useMemo(() => (exam ? InitialValuesService.getInitialValues(exam) : {}), [exam]);
  const navigate = useNavigate();

  const [isResultsFormVisible, setIsResultsFormVisible] = useState(true);

  // State for hierarchical tags
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Handle selected tags change from TagsFieldAccordion
  const handleSelectedTagsChange = useCallback((newSelectedTags) => {
    setSelectedTags(newSelectedTags);
  }, []);

  // Fetch tags with hierarchy from the API
  useEffect(() => {
    const fetchTags = async () => {
      try {
        if (apiClient.tagsClient?.getLevelTags) {
          const newTags = await apiClient.tagsClient.getLevelTags();
          setTags(newTags);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    fetchTags();
  }, []);

  // Initialize selectedTags based on exam tags
  useEffect(() => {
    if (exam?.examTagMapping?.length && tags.length > 0) {
      const initialSelected = tags.filter((tag) => exam.examTagMapping.some((mapping) => mapping.tagId === tag.id));
      setSelectedTags(initialSelected);
    } else if (!exam?.examTagMapping?.length) {
      // Clear selected tags if exam has no mappings (e.g. new exam or mappings removed)
      setSelectedTags([]);
    }
  }, [exam?.examTagMapping, tags]);

  const handleReadSubmit = useCallback(
    (data) => {
      if (!data.isValid) {
        return;
      }

      // Format selectedTags (TagModel[]) as examTagMapping objects
      data.values.tags = selectedTags.map((tag) => ({
        tagId: tag.id,
        examId: exam?.id,
      }));

      const submitType = data.event.nativeEvent.submitter.id;
      if (submitType === ExamReadSubmitType.APPROVE || submitType === ExamReadSubmitType.APPROVE_AND_NEXT) {
        if (!hasText(data.values.overRead)) {
          NotificationsService.displayError('Over-read content cannot be empty for approval.');
          return;
        }
        setIsResultsFormVisible(false);
      }
      handleSubmit(data);
    },
    [handleSubmit, selectedTags, exam?.id],
  );

  const handleCancelClick = useEvent(() => {
    navigate('/physician-dashboard');
  });

  useEffect(() => {
    setIsResultsFormVisible(!exam?.reads?.length || isUndo);
  }, [exam, isUndo]);

  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    const initModal = async () => {
      const templateResponse = await apiClient.templateClient.getTemplates(TemplateType.Read);
      setTemplates(templateResponse);
    };
    initModal();
  }, []);
  return (
    <StyledBodyDiv>
      <StyledAccordion alwaysOpen defaultActiveKey={['4', '5', '6', '9', '10']}>
        <PatientInfoAccordion eventKey="1" patient={exam?.patient} />
        <PriorExamsAccordion eventKey="2" exam={exam} />
        <CoordinatorInfoAccordion eventKey="3" coordinator={exam?.coordinator} />
        <Form
          id="exam-read-sidebar-form"
          initialValues={initialState}
          key={`${exam?.id}-${exam?.overRead}-read-form`}
          onSubmitClick={handleReadSubmit}
          render={({ onChange: valueSetter, valueGetter }) => {
            const studyType = valueGetter('service.description');
            const currentFormOrgan = valueGetter('organ');
            return (
              <FormElement>
                <ExamDetailsAccordion eventKey="4" exam={exam} studyType={studyType} valueGetter={valueGetter} valueSetter={valueSetter} />
                <ResultsAccordion
                  exam={exam}
                  isResultFormVisible={isResultsFormVisible}
                  valueGetter={valueGetter}
                  valueSetter={valueSetter}
                  template={templates}
                  textField="name"
                  dataItemKey="id"
                  fieldName="overRead"
                  currentFormOrgan={currentFormOrgan}
                />
                <TagsFieldAccordion eventKey="9" tags={tags} selectedTags={selectedTags} onSelectedTagsChange={handleSelectedTagsChange} />
              </FormElement>
            );
          }}
        />
      </StyledAccordion>

      <StyledButtonsContainerDiv>
        <StyledButtonsLeftContainerDiv>
          <Button disabled={!handlePrevExamClick} variant={ButtonVariants.SECONDARY} onClick={handlePrevExamClick}>
            {'<'}
          </Button>
          <Button disabled={!handleNextExamClick} variant={ButtonVariants.SECONDARY} onClick={handleNextExamClick}>
            {'>'}
          </Button>
        </StyledButtonsLeftContainerDiv>

        <StyledButtonsRightContainerDiv>
          <Button type="button" variant={ButtonVariants.SECONDARY} onClick={handleCancelClick}>
            Close
          </Button>
          {!isResultsFormVisible && (
            <Button disabled={exam?.locked} onClick={() => setIsResultsFormVisible(true)}>
              Add Addendum
            </Button>
          )}
          {isResultsFormVisible && (
            <>
              {isPreviewFlow && (
                <>
                  <Button form="exam-read-sidebar-form" id={ExamReadSubmitType.SAVE} type="submit">
                    Save
                  </Button>
                  <Button form="exam-read-sidebar-form" id={ExamReadSubmitType.FINALIZE} type="submit">
                    Finalize
                  </Button>
                </>
              )}

              {!isPreviewFlow && (
                <>
                  <Button form="exam-read-sidebar-form" id={ExamReadSubmitType.APPROVE} type="submit">
                    Approve
                  </Button>
                  <Button disabled={!handleNextExamClick} form="exam-read-sidebar-form" id={ExamReadSubmitType.APPROVE_AND_NEXT} type="submit">
                    Approve & Next
                  </Button>
                </>
              )}
            </>
          )}
        </StyledButtonsRightContainerDiv>
      </StyledButtonsContainerDiv>
    </StyledBodyDiv>
  );
};

const StyledButtonsContainerDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.palette.white};
  box-shadow: 0px -1px 4px rgba(0, 0, 0, 0.25);
  padding: ${({ theme }) => theme.space.spacing30};
`;

const StyledButtonsLeftContainerDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  column-gap: ${({ theme }) => theme.space.spacing30};
`;

const StyledButtonsRightContainerDiv = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: end;
  column-gap: ${({ theme }) => theme.space.spacing30};
`;

const StyledAccordion = styled(Accordion)`
  max-height: 93vh;
  overflow-y: auto;
`;

const StyledBodyDiv = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

ExamReadSidebarBody.propTypes = {
  exam: PropTypes.shape({
    coordinator: PropTypes.shape({}),
    id: PropTypes.number,
    locked: PropTypes.bool,
    location: PropTypes.shape({}),
    organ: PropTypes.number,
    patient: PropTypes.shape({}),
    reads: PropTypes.arrayOf(PropTypes.shape({})),
    service: PropTypes.shape({
      description: PropTypes.string,
    }),
    examTagMapping: PropTypes.arrayOf(
      PropTypes.shape({
        tagId: PropTypes.number,
      }),
    ),
  }),
  handleNextExamClick: PropTypes.func,
  handlePrevExamClick: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  adminTags: PropTypes.arrayOf(PropTypes.shape({})),
  isUndo: PropTypes.bool,
};

ExamReadSidebarBody.defaultProps = {
  exam: undefined,
  handleNextExamClick: undefined,
  handlePrevExamClick: undefined,
  adminTags: [],
  isUndo: false,
};

export { ExamReadSidebarBody };
